import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useApiTokens } from '@/hooks/useApiTokens';
import { KeyRound, Plus, Copy, Check, X, Trash2, Loader2, AlertTriangle } from 'lucide-react';

interface RevealedToken {
  token: string;
  name: string;
  prefix: string;
}

export default function ApiTokensPage() {
  const { tokens, loading, error, creating, createToken, revokeToken } = useApiTokens();

  const [name, setName] = useState('');
  const [nameHint, setNameHint] = useState(false);
  const [revealed, setRevealed] = useState<RevealedToken | null>(null);
  const [copied, setCopied] = useState(false);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const confirmTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (copyTimer.current) clearTimeout(copyTimer.current);
      if (confirmTimer.current) clearTimeout(confirmTimer.current);
    };
  }, []);

  const handleCreate = async () => {
    if (creating) return;
    if (!name.trim()) {
      setNameHint(true);
      document.getElementById('token-name')?.focus();
      return;
    }
    setNameHint(false);
    try {
      const created = await createToken(name.trim());
      setRevealed(created);
      setCopied(false);
      setName('');
    } catch {
      // error surfaced via hook state
    }
  };

  const handleCopy = async () => {
    if (!revealed) return;
    await navigator.clipboard.writeText(revealed.token);
    setCopied(true);
    if (copyTimer.current) clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopied(false), 2000);
  };

  const handleRevokeClick = async (id: string) => {
    if (confirmingId !== id) {
      setConfirmingId(id);
      if (confirmTimer.current) clearTimeout(confirmTimer.current);
      confirmTimer.current = setTimeout(() => setConfirmingId(null), 3000);
      return;
    }
    if (confirmTimer.current) clearTimeout(confirmTimer.current);
    setConfirmingId(null);
    setRevokingId(id);
    try {
      await revokeToken(id);
    } catch {
      // error surfaced via hook state
    } finally {
      setRevokingId(null);
    }
  };

  if (loading) {
    return (
      <div className="h-full bg-gradient-to-br from-background via-background to-accent/30 dark:to-accent/10 p-8 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="h-full bg-gradient-to-br from-background via-background to-accent/30 dark:to-accent/10 p-8 overflow-auto">
      <div className="max-w-2xl mx-auto">
        <header className="mb-8">
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <KeyRound className="h-6 w-6" />
            Tool Key
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Tool Keys for the AICS Pro API — paste one into an integration to connect it
          </p>
        </header>

        {error && (
          <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg mb-6">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Create Tool Key
            </CardTitle>
            <CardDescription>Give the Tool Key a name that describes where it will be used.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="token-name">Tool Key name</Label>
              <div className="flex gap-3">
                <Input
                  id="token-name"
                  placeholder="e.g. After Effects panel — workstation A"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (e.target.value.trim()) setNameHint(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCreate();
                  }}
                  className={nameHint ? 'border-destructive focus-visible:ring-destructive' : undefined}
                />
                <Button
                  onClick={handleCreate}
                  disabled={creating}
                  className={
                    'shrink-0 bg-gradient-to-r from-violet-600 to-violet-700 ' +
                    'hover:from-violet-700 hover:to-violet-800 ' +
                    'shadow-lg shadow-violet-500/25 transition-all ' +
                    'hover:shadow-xl hover:shadow-violet-500/30'
                  }
                >
                  {creating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create'
                  )}
                </Button>
              </div>
              {nameHint && <p className="text-xs text-destructive">Type a name for the Tool Key first.</p>}
            </div>

            {revealed && (
              <div className="rounded-lg border border-amber-300/60 dark:border-amber-500/30 bg-amber-50/60 dark:bg-amber-500/10 p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
                    <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                      Copy this Tool Key now — it will never be shown again.
                    </p>
                  </div>
                  <button
                    onClick={() => setRevealed(null)}
                    className={
                      'p-1 rounded-md text-amber-700/60 dark:text-amber-400/60 ' +
                      'hover:text-amber-800 dark:hover:text-amber-300 ' +
                      'hover:bg-amber-100/60 dark:hover:bg-amber-500/20 transition-colors shrink-0'
                    }
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <p className="text-xs text-amber-700 dark:text-amber-400/80">
                  Tool Key for <span className="font-medium">{revealed.name}</span>
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 min-w-0 font-mono text-xs bg-background border border-border rounded-md px-3 py-2.5 break-all select-all">
                    {revealed.token}
                  </code>
                  <Button variant="outline" size="icon" onClick={handleCopy} className="shrink-0">
                    {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5" />
              Your Tool Keys
            </CardTitle>
            <CardDescription>Tool Keys grant access to the API on your behalf. Revoke any you no longer use.</CardDescription>
          </CardHeader>
          <CardContent>
            {tokens.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                No Tool Keys yet. Create one to connect an integration.
              </p>
            ) : (
              <div className="divide-y divide-border/60">
                {tokens.map((token) => (
                  <div key={token.id} className="flex items-center gap-4 py-3 first:pt-0 last:pb-0">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground truncate">{token.name}</span>
                        <code className="font-mono text-xs text-muted-foreground shrink-0">{token.prefix}…</code>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Created {new Date(token.createdAt).toLocaleDateString()} · Last used:{' '}
                        {token.lastUsedAt ? new Date(token.lastUsedAt).toLocaleDateString() : 'Never'}
                      </p>
                    </div>
                    {confirmingId === token.id ? (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleRevokeClick(token.id)}
                        className="shrink-0 h-8"
                      >
                        Revoke?
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRevokeClick(token.id)}
                        disabled={revokingId === token.id}
                        className="shrink-0 h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                      >
                        {revokingId === token.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
